import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types'
import type { Database } from '@/integrations/supabase/types'

type Project = Tables<'projects'>
type ProjectInsert = TablesInsert<'projects'>
type ProjectUpdate = TablesUpdate<'projects'>
type ProjectStatus = Database['public']['Enums']['project_status']

export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          clients(id, name)
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
  })
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          clients(id, name, email),
          tasks(*)
        `)
        .eq('id', id)
        .maybeSingle()

      if (error) throw error
      return data
    },
    enabled: !!id,
  })
}

export function useProjectsByStatus(status: ProjectStatus) {
  return useQuery({
    queryKey: ['projects', 'status', status],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          clients(id, name)
        `)
        .eq('status', status)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
  })
}

export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (project: ProjectInsert & { estimated_hours?: number | null; budget?: number | null }) => {
      const { data, error } = await supabase
        .from('projects')
        .insert(project as ProjectInsert)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: ProjectUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['projects', variables.id] })
      
      // Snapshot previous value
      const previousProject = queryClient.getQueryData(['projects', variables.id])
      
      // Optimistically update
      queryClient.setQueryData(['projects', variables.id], (old: any) => {
        if (!old) return old
        return { ...old, ...variables }
      })
      
      return { previousProject }
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousProject) {
        queryClient.setQueryData(['projects', variables.id], context.previousProject)
      }
    },
    onSettled: (_, __, variables) => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects', variables.id] })
    },
  })
}

export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      // Delete related records first (cascade manually)
      // 1. Delete payments
      const { error: paymentsError } = await supabase
        .from('payments')
        .delete()
        .eq('project_id', id)
      
      if (paymentsError) throw paymentsError

      // 2. Delete time entries
      const { error: timeEntriesError } = await supabase
        .from('time_entries')
        .delete()
        .eq('project_id', id)
      
      if (timeEntriesError) throw timeEntriesError

      // 3. Delete tasks
      const { error: tasksError } = await supabase
        .from('tasks')
        .delete()
        .eq('project_id', id)
      
      if (tasksError) throw tasksError

      // 4. Finally delete the project
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['time_entries'] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

// Get total hours spent on a project
export function useProjectHours(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'hours'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('time_entries')
        .select('duration_minutes')
        .eq('project_id', projectId)

      if (error) throw error

      const totalMinutes = data?.reduce((acc, entry) => acc + ((entry as any).duration_minutes || 0), 0) || 0
      return Math.round(totalMinutes / 60)
    },
    enabled: !!projectId,
  })
}
